import type React from 'react';
import { useState } from 'react';

import * as styles from './styles';
import { Caption, ContainerDefault, DefaultInput, Divider, Loading, Placeholder, PlaceholderVehicleGroupIcon } from '..';
import { Button } from '@cobliteam/ui/components';

import { Location, UserPermission, type VehicleGroup } from '@cobliteam/common-types';

import { usePermissions } from '@cobliteam/hooks';

import Icon from '@cobliteam/ui/icons';

import { Checkbox } from '../SelectionControls';

let searchDebounceRef: any = null;
const searchDebounce = (fn: () => void, wait: number) => {
  if (searchDebounceRef) {
    clearTimeout(searchDebounceRef);
  }
  searchDebounceRef = setTimeout(fn, wait);
};

export interface VehicleGroupFilterOverlayProps {
  vehicleGroups: VehicleGroup[];
  selectedGroupsIds: Set<string>;
  selectionChanged: boolean;
  isApplying?: boolean;
  isLoading?: boolean;
  onGroupSelection: (group: VehicleGroup) => void;
  onClearFilter: () => void;
  onApplyFilter: () => void;
  onCancel: () => void;
}

/**
 * @deprecated This component is deprecated and will be removed in the future.
 * @description Now use '@cobliteam/ui/components/*' components.
 */
export const VehicleGroupFilterOverlay: React.FC<VehicleGroupFilterOverlayProps> = ({
  selectedGroupsIds,
  vehicleGroups,
  selectionChanged,
  isApplying,
  isLoading,
  onGroupSelection,
  onClearFilter,
  onApplyFilter,
  onCancel,
}) => {
  const [searchBoxInput, setSearchBoxInput] = useState('');
  const [selectedGroups] = useState(selectedGroupsIds);

  const { isAccessAllowed } = usePermissions();
  const hasUpdateVehiclePermission = isAccessAllowed(UserPermission.CREATE_UPDATE_DELETE_VEHICLE);

  const handleCreateGroup = () => {
    window.open(`${window.location.origin}${Location.VEHICLES}`, '_blank')!.focus();
  };

  const renderNoGroupsPlaceholder = () => (
    <div>
      <div className={styles.placeholderContainer}>
        <Placeholder
          className={styles.noGroupsPlaceholder}
          Icon={PlaceholderVehicleGroupIcon}
          text={
            hasUpdateVehiclePermission
              ? 'Ainda não existem grupos cadastrados. Crie grupos para ver melhor os dados da página.'
              : 'Não existem grupos de veículos. Para criar grupos, acesse a aba Frota (caso você não tenha acesso, solicite a outro usuário do painel).'
          }
        />
        {hasUpdateVehiclePermission ? <Button onClick={handleCreateGroup}>Criar Grupos</Button> : null}
      </div>
    </div>
  );

  const sortByName = (a: VehicleGroup, b: VehicleGroup) => {
    return a.name.toLowerCase().localeCompare(b.name.toLowerCase());
  };

  const sortBySelected = (a: VehicleGroup, b: VehicleGroup) => {
    if (selectedGroups.has(a.id) && !selectedGroups.has(b.id)) {
      return -1;
    }

    if (!selectedGroups.has(a.id) && selectedGroups.has(b.id)) {
      return 1;
    }

    return 0;
  };

  const filterOptionsBySearchBoxInput = () => {
    const normalizedSearchBoxInput = searchBoxInput.trim().toLowerCase();

    if (normalizedSearchBoxInput.length > 0) {
      return vehicleGroups.filter((group) => group.name.toLocaleLowerCase().match(normalizedSearchBoxInput));
    }

    return [...vehicleGroups];
  };

  const filteredOptions = filterOptionsBySearchBoxInput();

  const renderGroups = () =>
    filteredOptions
      .sort(sortByName)
      .sort(sortBySelected)
      .map((group) => {
        const disabled = group.vehicleIds && group.vehicleIds.length === 0;
        return (
          <div key={group.name} className={styles.chipWrapper}>
            {disabled && <Caption className={styles.tooltipText}>Grupo sem veículos</Caption>}
            <div>
              <Checkbox
                data-cy={`vehicle-group-filter-${group.id}`}
                disabled={disabled}
                checked={selectedGroupsIds.has(group.id)}
                onChange={() => onGroupSelection(group)}
                value={group.name}
                label={group.name}
              />
            </div>
          </div>
        );
      });

  const handleSearchBoxInputChange = ({ currentTarget: { value } }: React.ChangeEvent<HTMLInputElement>) => {
    searchDebounce(() => {
      setSearchBoxInput(value);
    }, 400);
  };

  return (
    <ContainerDefault className={styles.overlay}>
      {vehicleGroups.length > 0 ? (
        <>
          <Button variant='text' disabled={vehicleGroups.length === 0 || selectedGroupsIds.size === 0} className='m-2.5 mr-4 max-w-24 justify-start' onClick={onClearFilter}>
            Limpar filtros
          </Button>
          <Divider className={styles.contentDivider} />

          {isLoading ? (
            <div className={styles.loadingContainer}>
              <Loading />
            </div>
          ) : (
            <>
              <DefaultInput startAdornment={<Icon name='search' />} placeholder='Procurar grupos' onChange={handleSearchBoxInputChange} className={styles.searchBox} />
              <div className={styles.groupsContainerScroll}>
                <div className={styles.groupsContainer}>{renderGroups()}</div>
              </div>
            </>
          )}

          <Divider className={styles.contentDivider} />
          <div className={styles.filterActions}>
            <Button variant='text' className='mr-10 ml-auto' onClick={onCancel}>
              Cancelar
            </Button>
            <Button size='condensed' disabled={vehicleGroups.length === 0 || !selectionChanged} loading={isApplying} onClick={onApplyFilter}>
              Aplicar
            </Button>
          </div>
        </>
      ) : (
        renderNoGroupsPlaceholder()
      )}
    </ContainerDefault>
  );
};
